import React, { createContext, useContext, useEffect, useRef, useState } from "react";

type TopNavMenuContextValue = {
  activeMenu: string | null;
  setActiveMenu: (menu: string | null) => void;
  openMenu: (menu: string) => void;
  closeMenu: () => void;
  closeMenuDelayed: () => void;
  cancelClose: () => void;
};

const TopNavMenuContext = createContext<TopNavMenuContextValue | null>(null);

export const TopNavMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openMenu = (menu: string) => {
    cancelClose();
    setActiveMenu(menu);
  };

  const closeMenu = () => {
    cancelClose();
    setActiveMenu(null);
  };

  const closeMenuDelayed = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
      closeTimerRef.current = null;
    }, 220);
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <TopNavMenuContext.Provider value={{ activeMenu, setActiveMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose }}>
      <div ref={rootRef} className="flex items-center gap-2">
        {children}
      </div>
    </TopNavMenuContext.Provider>
  );
};

export const useTopNavMenu = () => {
  const ctx = useContext(TopNavMenuContext);
  if (!ctx) throw new Error("useTopNavMenu must be used within TopNavMenuProvider");
  return ctx;
};
